import { useContext } from 'react';
import { PdfContext } from 'utils/context';
import { v1 } from 'uuid';
import { lengthInInterval, truncateTab } from '../Generics/utilsFunctions';
import '../Tables/table.style.scss';
import './keyNumberNitrogenPage.style.scss';
import fleche from 'assets/images/fleche.png';
import { useTranslation } from 'react-i18next';

/**
 * Filtre les rows azote pour le PDF :
 * - On garde tout
 * - Sauf les enfants de nitrogenFixerSum et othersSum
 *   (les cultures individuelles — hierarchy.length === 3)
 */
const filterNitrogenRows = (rows) => {
  if (!rows?.length) return [];
  return rows.filter((row) => {
    const hierarchy = row.hierarchy ?? [row.id];
    // profondeur 3 = enfant d'un gainCategory → on exclut
    return hierarchy.length < 3;
  });
};

/**
 * Construit les headers à partir des colonnes de NitrogenKeyData.
 * Les colonnes ont les fields : 'indicator', optionnellement 'previous', 'year_0', 'year_1'...
 */
const buildHeaders = (rows, forecastCampaign, suggest, previous, t) => {
  if (!rows?.length) return [];

  const headers = [t('simulation.common.campaign')];

  if (previous?.nitrogen) {
    const prevYear = forecastCampaign.startYear - 1;
    headers.push(`${prevYear} - ${prevYear + 1}`);
  }

  suggest?.years?.forEach((_, i) => {
    const startYear = forecastCampaign.startYear + i;
    headers.push(`${startYear}-${startYear + 1}`);
  });

  return headers;
};

/**
 * Récupère les valeurs affichables d'une row dans l'ordre des colonnes.
 * Une row NitrogenKeyData a les champs : previous (optionnel), year_0, year_1...
 */
const getRowValues = (row, hasPrevious, nbYears) => {
  const values = [];

  if (hasPrevious) {
    values.push(row.previous != null ? Math.round(row.previous).toString() : '-');
  }

  for (let i = 0; i < nbYears; i++) {
    const val = row[`year_${i}`];
    values.push(val != null ? Math.round(val).toString() : '-');
  }

  return values;
};

const KeyNumberNitrogenPage = () => {
  const { t } = useTranslation();
  const { keyNumberNitrogen, forecastCampaign, suggest, previous } = useContext(PdfContext);

  if (!keyNumberNitrogen?.length) return null;

  const filteredRows = filterNitrogenRows(keyNumberNitrogen);
  const headers = buildHeaders(filteredRows, forecastCampaign, suggest, previous, t);
  const hasPrevious = !!previous?.nitrogen;
  const nbYears = suggest?.years?.length ?? 0;

  const numberOfRowPerPage = 12;
  let toDuplicate = true;
  let repetition = 1;
  const pages = [];

  while (toDuplicate) {
    const truncatedResult = truncateTab(filteredRows, numberOfRowPerPage, repetition);
    pages.push(
      buildTable(
        truncatedResult,
        headers,
        hasPrevious,
        nbYears,
        repetition > 1 ? t('simulation.common.continued') : '',
        t,
      ),
    );
    lengthInInterval(filteredRows, repetition, numberOfRowPerPage)
      ? repetition++
      : (toDuplicate = false);
  }

  return pages;
};

const buildTable = (rows, headers, hasPrevious, nbYears, message, t) => {
  return (
    <article className="pdf-page" key={v1()}>
      <div className="tableau_center">
        <h6 className="title_h6 title_key">
          {t('simulation.result.pdf.nitrogen.title')} {message}
        </h6>
        <table className="table">
          <thead className="table_head">
            <tr className="key_table_tr">
              {headers.map((header, i) => (
                <th className="column_name" key={i} scope="col">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row) => {
              const values = getRowValues(row, hasPrevious, nbYears);
              const isChild = (row.hierarchy?.length ?? 1) > 1;

              return (
                <tr className="key_table_tr nitrogen_tr" key={v1()}>
                  <td className={`value_table ${isChild ? 'nitrogen_td' : 'row_label'}`}>
                    {isChild ? (
                      <div className="inline_icon_cell nitrogen_culture">
                        <img className="cell-icon" src={fleche} alt="Logo fleche" />
                        <span>{row.indicator}</span>
                      </div>
                    ) : (
                      row.indicator
                    )}
                  </td>
                  {values.map((val) => (
                    <td className="value_table" key={v1()}>
                      {val}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
};

export default KeyNumberNitrogenPage;
